import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Paperclip, Eye, Download, Trash, FloppyDisk, X, Plus, PencilSimple } from "@phosphor-icons/react";
import api, { apiErrorMessage } from "@/lib/api";
import ChangeLogNotes from "@/components/shared/ChangeLogNotes";

const DEFAULT_TYPES = ["License", "Permit", "Reminder"];

function _fmtDateMDY(s) {
  if (!s) return "—";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function LicenseModal({ item, customCols = [], sections = [], itemTypeSuggestions, onSectionsChange, onClose, onChange }) {
  const [form, setForm] = useState(null);
  const [attachments, setAttachments] = useState({});
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [newSectionLabel, setNewSectionLabel] = useState("");
  const [addingSection, setAddingSection] = useState(false);
  const [renamingSectionId, setRenamingSectionId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const fileRefs = useRef({});

  useEffect(() => { setForm(item ? { ...item } : null); setDirty(false); }, [item]);

  const loadAtt = useCallback(async (lid) => {
    try { const { data } = await api.get(`/licenses/${lid}/attachments`); setAttachments(data || {}); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { if (item?.id) loadAtt(item.id); }, [item?.id, loadAtt]);

  if (!item || !form) return null;
  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ["id","item_seq","created_at","updated_at","created_by_id","created_by_name","updated_by_id","updated_by_name","has_attachments"].forEach((k) => delete payload[k]);
      const { data } = await api.put(`/licenses/${item.id}`, payload);
      onChange && onChange(data);
      setDirty(false);
      toast.success("Item saved");
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const upload = async (sectionId, file) => {
    if (!file) return;
    setUploading(sectionId);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/licenses/${item.id}/attachments?section_id=${sectionId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await loadAtt(item.id);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setUploading(""); }
  };

  const remove = async (fid) => {
    try { await api.delete(`/licenses/${item.id}/attachments/${fid}`); await loadAtt(item.id); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const addSection = async () => {
    const label = newSectionLabel.trim();
    if (!label) return;
    setAddingSection(true);
    try {
      await api.post("/licenses/attachment-sections", { label });
      setNewSectionLabel("");
      onSectionsChange && onSectionsChange();
      toast.success(`Added section "${label}"`);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setAddingSection(false); }
  };

  const renameSection = async () => {
    const label = renameValue.trim();
    if (!label || !renamingSectionId) return;
    try {
      await api.put(`/licenses/attachment-sections/${renamingSectionId}`, { label });
      setRenamingSectionId(null); setRenameValue("");
      onSectionsChange && onSectionsChange();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const removeSection = async (s) => {
    if (s.is_default) { toast.error("Default sections can't be removed"); return; }
    if (!window.confirm(`Remove section "${s.label}"? Uploaded files remain accessible via their record but this section will no longer appear.`)) return;
    try {
      await api.delete(`/licenses/attachment-sections/${s.id}`);
      onSectionsChange && onSectionsChange();
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const del = async () => {
    if (!window.confirm(`Delete item #${item.item_seq}?`)) return;
    try { await api.delete(`/licenses/${item.id}`); toast.success("Item deleted"); onClose(); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const attachUrl = (fid, dl) => `${backend}/api/licenses/${item.id}/attachments/${fid}${dl ? "?download=true" : ""}`;
  const kpis = [
    { label: "Item Type", value: form.item_type || "—" },
    { label: "Item Name", value: form.item_name || "—" },
    { label: "Start Date", value: _fmtDateMDY(form.start_date) },
    { label: "Expiration Date", value: _fmtDateMDY(form.expiration_date) },
  ];

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl" data-testid="license-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Item #{item.item_seq}{form.item_name ? ` · ${form.item_name}` : ""}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={del} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                <Trash size={12} className="mr-1" /> Delete
              </Button>
              <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="license-modal-save">
                <FloppyDisk size={12} className="mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="license-modal-kpis">
              {kpis.map((k) => (
                <div key={k.label} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">{k.label}</div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5 truncate" title={String(k.value)}>{k.value}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <label className="block text-[10px]">
                <span className="uppercase tracking-widest text-slate-500 font-semibold">Item Type</span>
                <Input list="license-modal-item-type-options" value={form.item_type || ""} onChange={(e) => setField("item_type", e.target.value)}
                  placeholder="License / Permit / …" className="text-xs h-7 mt-0.5" data-testid="license-item-type" />
                <datalist id="license-modal-item-type-options">
                  {(itemTypeSuggestions && itemTypeSuggestions.length > 0 ? itemTypeSuggestions : DEFAULT_TYPES).map((t) => <option key={t} value={t} />)}
                </datalist>
              </label>
              <label className="block text-[10px]">
                <span className="uppercase tracking-widest text-slate-500 font-semibold">Item Name</span>
                <Input value={form.item_name ?? ""} onChange={(e) => setField("item_name", e.target.value)} className="text-xs h-7 mt-0.5" data-testid="license-item-name" />
              </label>
              <label className="block text-[10px]">
                <span className="uppercase tracking-widest text-slate-500 font-semibold">Start Date</span>
                <Input type="date" value={form.start_date ?? ""} onChange={(e) => setField("start_date", e.target.value || null)} className="text-xs h-7 mt-0.5" />
              </label>
              <label className="block text-[10px]">
                <span className="uppercase tracking-widest text-slate-500 font-semibold">Expiration Date</span>
                <Input type="date" value={form.expiration_date ?? ""} onChange={(e) => setField("expiration_date", e.target.value || null)} className="text-xs h-7 mt-0.5" />
              </label>
            </div>

            {customCols.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-2 mb-2">Custom fields</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {customCols.map((c) => (
                    <label key={c.id} className="block text-[10px]">
                      <span className="uppercase tracking-widest text-slate-500 font-semibold">{c.label}</span>
                      <Input
                        type={c.type === "date" ? "date" : (c.type === "number" || c.type === "money" || c.type === "percent") ? "number" : "text"}
                        value={(form.custom_fields || {})[c.key] ?? ""}
                        onChange={(e) => setField("custom_fields", { ...(form.custom_fields || {}), [c.key]: (c.type === "text" || c.type === "date") ? (e.target.value || "") : (e.target.value === "" ? null : Number(e.target.value)) })}
                        className="text-xs h-7 mt-0.5"
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mt-2 mb-2 flex items-center justify-between">
                <span>Attachments</span>
                <div className="flex items-center gap-1">
                  <Input value={newSectionLabel} onChange={(e) => setNewSectionLabel(e.target.value)} placeholder="New section name…"
                    className="h-6 text-[10px] w-40" data-testid="license-new-section-input" />
                  <Button size="sm" onClick={addSection} disabled={addingSection || !newSectionLabel.trim()} className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid="license-add-section">
                    <Plus size={10} className="mr-0.5" /> Add section
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="license-modal-attachments">
                {sections.map((s) => {
                  const versions = attachments[s.id] || [];
                  const current = versions[0];
                  const prev = versions.slice(1);
                  const isRenaming = renamingSectionId === s.id;
                  return (
                    <div key={s.id} className="rounded-md border border-slate-200 bg-slate-50/50 p-2" data-testid={`license-attach-section-${s.id}`}>
                      <div className="flex items-center justify-between mb-1 gap-1">
                        {isRenaming ? (
                          <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="h-5 text-[10px] flex-1"
                            onKeyDown={(e) => { if (e.key === "Enter") renameSection(); if (e.key === "Escape") { setRenamingSectionId(null); setRenameValue(""); } }} autoFocus />
                        ) : (
                          <span className="text-[10px] font-bold text-slate-700 truncate flex-1" title={s.label}>{s.label}{s.is_default ? " (default)" : ""}</span>
                        )}
                        {!s.is_default && !isRenaming && (
                          <>
                            <button type="button" onClick={() => { setRenamingSectionId(s.id); setRenameValue(s.label); }} className="text-[9px] text-slate-500 hover:text-[#1f64c2]" title="Rename section"><PencilSimple size={9} /></button>
                            <button type="button" onClick={() => removeSection(s)} className="text-[9px] text-rose-500 hover:text-rose-700" title="Remove section"><X size={9} /></button>
                          </>
                        )}
                        {isRenaming && (
                          <>
                            <button type="button" onClick={renameSection} className="text-[9px] text-emerald-600">Save</button>
                            <button type="button" onClick={() => { setRenamingSectionId(null); setRenameValue(""); }} className="text-[9px] text-slate-500">Cancel</button>
                          </>
                        )}
                        <button type="button" onClick={() => fileRefs.current[s.id]?.click()} disabled={uploading === s.id}
                          className="text-[10px] font-semibold text-[#1f64c2] hover:text-[#327de3] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-white"
                          data-testid={`license-attach-add-${s.id}`}>
                          <Paperclip size={10} /> {uploading === s.id ? "…" : "Add"}
                        </button>
                        <input ref={(el) => { fileRefs.current[s.id] = el; }} type="file" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(s.id, f); e.target.value = ""; }} />
                      </div>
                      {!current ? <div className="text-[10px] text-slate-400 italic">No files uploaded.</div> : (
                        <div className="flex items-center gap-1 text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5">
                          <span className="font-semibold text-slate-500">v{current.version}</span>
                          <span className="truncate flex-1" title={current.filename}>{current.filename}</span>
                          <a href={attachUrl(current.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2]"><Eye size={10} /></a>
                          <a href={attachUrl(current.id, true)} className="text-[#1f64c2]"><Download size={10} /></a>
                          <button onClick={() => remove(current.id)} className="text-rose-500"><Trash size={10} /></button>
                        </div>
                      )}
                      {prev.length > 0 && (
                        <details className="mt-1">
                          <summary className="text-[9px] uppercase tracking-widest font-semibold text-slate-500 cursor-pointer">Previous ({prev.length})</summary>
                          <div className="mt-0.5 space-y-0.5 pl-2 border-l border-slate-200">
                            {prev.map((v) => (
                              <div key={v.id} className="flex items-center gap-1 text-[9px]">
                                <span className="font-semibold text-slate-500">v{v.version}</span>
                                <span className="truncate flex-1" title={v.filename}>{v.filename}</span>
                                <a href={attachUrl(v.id)} target="_blank" rel="noreferrer" className="text-[#1f64c2]"><Eye size={9} /></a>
                                <a href={attachUrl(v.id, true)} className="text-[#1f64c2]"><Download size={9} /></a>
                                <button onClick={() => remove(v.id)} className="text-rose-500"><Trash size={9} /></button>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <ChangeLogNotes basePath={`/licenses/${item.id}`} idFieldLabel="item" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={13} className="mr-1" /> Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
